import { Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { CustomFormValidators } from "@app/directives/_forms/form-validation.service";
import { IUser } from "@app/interfaces/IUser";
import { CurrentUserService } from "@app/services/currentUser.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, from, Observable, of } from "rxjs";
import { map, take } from "rxjs/operators";
import { IReseller } from "../../../_models/IReseller";
import { IUserGroup } from "../../../_models/IUserGroup";
import { AccountService } from "../../../_services/account.service";
import { EngineerService } from "../../../_services/engineer.service";
import { UserGroupService } from "../../../_services/user-group.service";
import { UserService } from "../../../_services/user.service";
import './engineer-editor.component.scss';

@UntilDestroy()
@Component({
    selector: "engineer-editor",
    template: require("./engineer-editor.component.html")
})
export class EngineerEditorComponent implements OnChanges {
    @Input() engineer: IUser;

    @Output() engineerSaved = new EventEmitter<number>();

    groups: IUserGroup[];

    hasReseller: boolean;
    selectedResellerId: number;
    resellers: IReseller[];

    engineerEditorForm: FormGroup;
    saving: boolean;

    currentUser: IUser;

    constructor(
        private readonly engineerService: EngineerService,
        private readonly userGroupService: UserGroupService,
        private readonly userService: UserService,
        private readonly currentUserService: CurrentUserService,
        private readonly accountService: AccountService) { }
    
    ngOnChanges(): void {
        this.loadData();
    }

    saveEngineer(): void {
        this.saving = true;

        this.engineerService.saveEngineer(this.engineer).pipe(take(1)).subscribe(user => {
            this.engineerSaved.emit(user);
            this.saving = false;
        });
    }

    groupSearchPredicate(group: IUserGroup, text: string): boolean {
        return group.Description.toLowerCase().includes(text.toLowerCase());
    }

    onGroupSelected(group: IUserGroup): void {
        if (!group) {
            return;
        }
        this.engineerEditorForm.get("UserGroupId").setValue(group.Id);
    }

    get formIsValid(): boolean {
        return this.engineerEditorForm.valid;
    }

    get userGroupId(): number {
        return this.engineerEditorForm.value.UserGroupId;
    }

    get EmailExistsValidator(): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors> => {
            if (this.currentUser.Email.toLowerCase() == (control.value as string).toLowerCase()) {
                return of({ emailAlreadyExists: true })
            }

            return this.userService.emailExists(control.value)
                .pipe(map((result: boolean) => result ? { emailAlreadyExists: true } : null));
        };
    }

    //--

    private loadData(): void {
        if (!this.engineer || this.engineerEditorForm) {
            return;
        }

        this.setupForm(this.engineer);

        combineLatest([
            this.userGroupService.getUserGroups(),
            from(this.currentUserService.getCurrentUser() as Promise<IUser>),
            this.accountService.getResellers()
        ]).pipe(
            untilDestroyed(this)
        ).subscribe(([groups, currentUser, resellers]) => {
            this.groups = groups;
            this.currentUser = currentUser;
            this.resellers = resellers;
            this.selectedResellerId = this.engineer.ResellerIds?.firstOrDefault();
        });
    }

    private setupForm(engineer: IUser): void {
        this.engineerEditorForm = new FormGroup({
            'Name': new FormControl(engineer.Description, [Validators.required, CustomFormValidators.emptyWhiteSpace]),
            'Email': new FormControl(engineer.Email, [Validators.required, Validators.email], !!engineer.Id ? null : [this.EmailExistsValidator]),
            'UserGroupId': new FormControl(engineer.UserGroupId, [Validators.required]),
        });

        this.engineerEditorForm.statusChanges.pipe(untilDestroyed(this)).subscribe((status: string) => {
            if (status === "INVALID" || !this.currentUser) {
                return;
            }

            this.engineer = {
                ...this.engineer,
                Description: this.engineerEditorForm.value.Name,
                Email: this.engineerEditorForm.value.Email,
                UserGroupId: this.engineerEditorForm.value.UserGroupId
            }
        });
    
    }
}
